import { zodResolver } from '@hookform/resolvers/zod/dist/zod'
import { LoadingButton } from '@mui/lab'
import { Box, Stack } from '@mui/material'
import { addUser } from 'api/users'
import FormSelect from 'components/form-elements/FormSelect'
import { EMAIL_IS_INVALID, FIELD_IS_REQUIRED, minButtonWidth } from 'const'
import { UIModal } from 'features/UI'
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { UserType } from 'types'
import { object, string, TypeOf } from 'zod'
import FormInput from '../form-elements/FormInput'
import NotificationSys from '../NotificationSystem'

interface Props {
  isOpen: boolean
  handleClose: () => void
}

const formSchema = object({
  name: string().min(1, FIELD_IS_REQUIRED),
  lastName: string().min(1, FIELD_IS_REQUIRED),
  email: string().min(1, FIELD_IS_REQUIRED).email(EMAIL_IS_INVALID),
  role: string().min(1, FIELD_IS_REQUIRED),
  phoneNumber: string().min(10).optional().or(string().length(0)),
})

type FormType = TypeOf<typeof formSchema>

const ModalContent = ({ handleClose }: { handleClose: () => void }) => {
  const formSettings = {
    mode: 'onChange' as const,
    resolver: zodResolver(formSchema),
  }

  const formProps = useForm<FormType>(formSettings)
  const { handleSubmit, formState } = formProps

  const addUserMutation = useMutation(addUser, {
    onSuccess: () => {
      handleClose()
      NotificationSys.showSuccess(
        'User created successfully. Please check your email for the login details.',
      )
    },
  })

  const onSubmitHandler: SubmitHandler<FormType> = ({
    name,
    lastName,
    email,
    role,
    phoneNumber,
  }: FormType) => {
    addUserMutation.mutate({ name, lastName, email, type: role as UserType, phoneNumber })
  }

  const roles = [
    { label: 'Admin', value: UserType.ADMIN },
    { label: 'Analyst', value: UserType.ANALYST },
  ]

  return (
    <FormProvider {...formProps}>
      <Box component="form" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
        <Stack spacing={2}>
          <FormInput label="Name" name="name" required />
          <FormInput label="Last Name" name="lastName" required />
          <FormInput label="Email" name="email" required />
          <FormSelect label="Role" name="role" required options={roles} />
          <FormInput label="Mobile Phone Number" name="phoneNumber" />
        </Stack>

        <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 4 }}>
          <LoadingButton variant="outlined" onClick={handleClose} sx={minButtonWidth}>
            Close
          </LoadingButton>
          <LoadingButton
            loading={addUserMutation.isLoading}
            disabled={!formState.isValid}
            variant="contained"
            type="submit"
            sx={minButtonWidth}
          >
            Create
          </LoadingButton>
        </Stack>
      </Box>
    </FormProvider>
  )
}

export const AddAdminModal = ({ isOpen, handleClose }: Props) => (
  <UIModal title="Create User" isOpen={isOpen} onClose={handleClose}>
    <ModalContent handleClose={handleClose} />
  </UIModal>
)
